import React, { useCallback, useEffect, useState } from 'react';
import type { VFC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import { useCoordination } from '@atlassiansox/engagekit-ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Modal, {
	ModalTransition,
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';

import { useCoordinationClient } from '@confluence/engagement-provider';
import { useSpaceId } from '@confluence/space-utils';

import { ExternalCollabFeaturesList } from './ExternalCollabFeaturesList';
import { LEARN_MORE_LINK } from './constants';
import { GuestOnboardingModalImage } from './GuestOnboardingModalImage';

const i18n = defineMessages({
	title: {
		id: 'external-collab-ui.guest-onboarding-modal.title',
		defaultMessage: 'Welcome, Confluence guest!',
		description: 'Title at the top of the guest onboarding modal',
	},
	subtitle: {
		id: 'external-collab-ui.guest-onboarding-modal.subtitle',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"As a guest, some Confluence functionality isn't available to you. By default, you can:",
		description: 'Subtitle at the top of the guest onboarding modal',
	},
	footer: {
		id: 'external-collab-ui.guest-onboarding-modal.footer',
		defaultMessage:
			'<b>Note:</b> Your admin may further restrict your functionality from these default settings.',
		description: 'Footer note at the bottom of the guest onboarding modal',
	},
	learnMoreButton: {
		id: 'external-collab-ui.guest-onboarding-modal.learn-more',
		defaultMessage: 'Learn more',
		description: 'Text for a learn more button at the bottom of the modal',
	},
	okButton: {
		id: 'external-collab-ui.guest-onboarding-modal.ok-button',
		defaultMessage: 'OK',
		description: 'Text for an OK button at the bottom of the modal',
	},
	imgAltText: {
		id: 'external-collab-ui.guest-onboarding-modal.img-alt-text',
		defaultMessage: 'Guest Onboarding',
		description: 'Alt text for the guest onboarding header image',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalImageHeader = styled.div({
	position: 'relative',
	height: '150px',
	backgroundColor: token('color.background.accent.teal.subtlest', '#E3FAFC'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DismissButton = styled.div({
	position: 'absolute',
	top: token('space.200', '16px'),
	right: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.div({
	margin: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Subtitle = styled.div({
	textAlign: 'left',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeaturesListWrapper = styled.div({
	margin: `auto ${token('space.500', '40px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterNote = styled.div({
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
	marginBottom: token('space.100', '18px'),
	textAlign: 'left',
});

export const GuestOnboardingModal: VFC = () => {
	const ANALYTICS_SOURCE = 'guestsOnboardingDialog';
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const intl = useIntl();
	const spaceId = useSpaceId();

	// This EP message ID's audience is only to Confluence Guests,
	// so we do not need to check isExternalCollaborator again
	const epMessageId = 'confluence-guest-onboarding-modal';
	const coordinationClient = useCoordinationClient();
	const [startOnboarding, stopOnboarding] = useCoordination(coordinationClient, epMessageId);

	const onClose = useCallback(() => {
		setIsOpen(false);
		void stopOnboarding();
	}, [stopOnboarding]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// send analytics screen event
		if (isOpen) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: ANALYTICS_SOURCE,
					attributes: {
						containerId: spaceId,
					},
				},
			}).fire();
		}
	}, [spaceId, createAnalyticsEvent, isOpen]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissed',
				source: ANALYTICS_SOURCE,
				containerType: 'space',
				containerId: spaceId,
			},
		}).fire();
		onClose();
	}, [ANALYTICS_SOURCE, spaceId, onClose, createAnalyticsEvent]);

	const handleConfirm = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'confirm',
				source: ANALYTICS_SOURCE,
				containerType: 'space',
				containerId: spaceId,
			},
		}).fire();
		onClose();
	}, [ANALYTICS_SOURCE, spaceId, onClose, createAnalyticsEvent]);

	const handleLearnMoreClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'learnMoreLink',
				source: ANALYTICS_SOURCE,
				containerType: 'space',
				containerId: spaceId,
			},
		}).fire();
	}, [ANALYTICS_SOURCE, spaceId, createAnalyticsEvent]);

	useEffect(() => {
		/*
    Avoid adding additional dependencies to open this modal because
    `start` message to Engagement Platform is called already through useCoordination hook.
    If you add another dependency here, then `start` message can be called and
    Engagement Platform can consider this message already seen or discovered,
    even though modal is not shown or open.
    */
		if (startOnboarding) {
			setIsOpen(true);
		}
	}, [startOnboarding]);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal shouldScrollInViewport onClose={handleDismiss} width="400px">
					<ModalImageHeader>
						<DismissButton>
							<Button
								data-testId="guest-onboarding-close-button"
								appearance="subtle-link"
								onClick={handleDismiss}
								spacing="none"
							>
								<CrossIcon label="" />
							</Button>
						</DismissButton>
						<GuestOnboardingModalImage alt={intl.formatMessage(i18n.imgAltText)} />
					</ModalImageHeader>
					<ModalHeader>
						<TitleWrapper>
							<ModalTitle>
								<FormattedMessage {...i18n.title} />
							</ModalTitle>
						</TitleWrapper>
					</ModalHeader>
					<ModalBody>
						<FeaturesListWrapper>
							<Subtitle>
								<FormattedMessage {...i18n.subtitle} />
							</Subtitle>
							<ExternalCollabFeaturesList
								footer={
									<FooterNote>
										<FormattedMessage
											{...i18n.footer}
											values={{
												b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
											}}
										/>
									</FooterNote>
								}
							/>
						</FeaturesListWrapper>
					</ModalBody>
					<ModalFooter>
						<Button
							appearance="link"
							href={LEARN_MORE_LINK}
							target="_blank"
							onClick={handleLearnMoreClick}
						>
							<FormattedMessage {...i18n.learnMoreButton} />
						</Button>
						<Button appearance="primary" onClick={handleConfirm}>
							<FormattedMessage {...i18n.okButton} />
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
